/**
 * Portão de leitura de QR Code — núcleo puro, sem React e sem câmera.
 *
 * Concentra as três regras que antes viviam espalhadas e duplicadas nos
 * consumidores: leitura única (`scannedRef` do leitor da bottom nav), dedupe
 * por sessão com cooldown (fila + `Set` + timestamp do desafio de nível) e o
 * rearme do código quando o backend rejeita a resposta.
 *
 * O decodificador entrega o mesmo código dezenas de vezes por segundo enquanto
 * o QR Code está enquadrado. Cabe ao portão decidir quais dessas ofertas viram
 * uma chamada de negócio.
 */

export type ScanGateMode = 'single' | 'continuous'

export type ScanRejectionReason =
  | 'empty' // string vazia depois do trim
  | 'closed' // modo single já entregou uma leitura aceita
  | 'busy' // consumidor ainda está processando a leitura anterior
  | 'cooldown' // mesma janela de tempo da leitura anterior
  | 'duplicate' // código já aceito nesta sessão de leitura

export type ScanOffer =
  | { accepted: true; code: string }
  | { accepted: false; reason: ScanRejectionReason }

export type ScanOutcome =
  /** O consumidor deu a leitura por boa — o código continua bloqueado. */
  | 'accepted'
  /** O consumidor recusou (ex.: resposta errada) — o código volta a valer. */
  | 'rejected'

export type ScanGateOptions = {
  mode?: ScanGateMode
  /** Intervalo mínimo entre duas leituras aceitas. 0 desliga. */
  cooldownMs?: number
  /** Injetável para teste — nunca chamar `Date.now()` direto no núcleo. */
  now?: () => number
}

export type ScanGate = {
  offer: (raw: unknown) => ScanOffer
  settle: (code: string, outcome: ScanOutcome) => void
  reset: () => void
  isBusy: () => boolean
  isOpen: () => boolean
}

export const createScanGate = ({
  mode = 'single',
  cooldownMs = 0,
  now = () => Date.now()
}: ScanGateOptions = {}): ScanGate => {
  let closed = false
  let busy = false
  let lastAcceptedAt: number | null = null
  const seen = new Set<string>()

  const offer = (raw: unknown): ScanOffer => {
    const code = typeof raw === 'string' ? raw.trim() : ''

    if (!code) return { accepted: false, reason: 'empty' }
    if (closed) return { accepted: false, reason: 'closed' }
    if (busy) return { accepted: false, reason: 'busy' }

    if (
      cooldownMs > 0 &&
      lastAcceptedAt !== null &&
      now() - lastAcceptedAt < cooldownMs
    ) {
      return { accepted: false, reason: 'cooldown' }
    }

    if (seen.has(code)) return { accepted: false, reason: 'duplicate' }

    busy = true
    seen.add(code)
    lastAcceptedAt = now()
    if (mode === 'single') closed = true

    return { accepted: true, code }
  }

  const settle = (code: string, outcome: ScanOutcome) => {
    busy = false

    if (outcome === 'rejected') {
      seen.delete(code)
      closed = false
      // Libera o cooldown junto: recusa é convite explícito a tentar de novo.
      lastAcceptedAt = null
    }
  }

  const reset = () => {
    closed = false
    busy = false
    lastAcceptedAt = null
    seen.clear()
  }

  return {
    offer,
    settle,
    reset,
    isBusy: () => busy,
    isOpen: () => !closed
  }
}
